<template>
  <div class="page-wrapper privacy-page-wrapper">
    <div class="privacy-page">

      <h1 class="page-title">Privacy Policy</h1>
      <p class="last-updated">Last updated January 28, 2026</p>

      <div class="privacy-content">

        <p>This Privacy Notice for Organ Media Foundation ("we," "us," or "our"), describes how and why we might access, collect, store, use, and/or share ("process") your personal information when you use our services ("Services"), including when you:</p>

        <ul>
          <li>Download and use our mobile application (Organlive), or any other application of ours that links to this Privacy Notice</li>
          <li>Engage with us in other related ways, including any marketing or events</li>
        </ul>

        <p><strong>Questions or concerns?</strong> Reading this Privacy Notice will help you understand your privacy rights and choices. We are responsible for making decisions about how your personal information is processed. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at <a href="mailto:comments@organlive.com" class="link-primary">comments@organlive.com</a>.</p>

        <!-- Summary of Key Points -->
        <h2>SUMMARY OF KEY POINTS</h2>
        <p>This summary provides key points from our Privacy Notice, but you can find out more details about any of these topics by clicking the link following each key point or by using our table of contents below to find the section you are looking for.</p>

        <p><strong>What personal information do we process?</strong> When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services, the choices you make, and the products and features you use. Learn more about <a href="#section1" class="link-primary">personal information you disclose to us</a>.</p>

        <p><strong>Do we process any sensitive personal information?</strong> Some of the information may be considered "special" or "sensitive" in certain jurisdictions, for example your racial or ethnic origins, sexual orientation, and religious beliefs. We do not process sensitive personal information.</p>

        <p><strong>Do we collect any information from third parties?</strong> We do not collect any information from third parties.</p>

        <p><strong>How do we process your information?</strong> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent. We process your information only when we have a valid legal reason to do so. Learn more about <a href="#section2" class="link-primary">how we process your information</a>.</p>

        <p><strong>In what situations and with which parties do we share personal information?</strong> We may share information in specific situations and with specific third parties. Learn more about <a href="#section4" class="link-primary">when and with whom we share your personal information</a>.</p>

        <p><strong>What are your rights?</strong> Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information. Learn more about <a href="#section7" class="link-primary">your privacy rights</a>.</p>

        <p><strong>How do you exercise your rights?</strong> The easiest way to exercise your rights is by submitting a data subject access request, or by contacting us. We will consider and act upon any request in accordance with applicable data protection laws.</p>

        <p><strong>Want to learn more about what we do with any information we collect?</strong> Review the Privacy Notice in full.</p>

        <!-- Table of Contents -->
        <h2>TABLE OF CONTENTS</h2>
        <ol class="toc">
          <li><a href="#section1" class="link-primary">WHAT INFORMATION DO WE COLLECT?</a></li>
          <li><a href="#section2" class="link-primary">HOW DO WE PROCESS YOUR INFORMATION?</a></li>
          <li><a href="#section3" class="link-primary">WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?</a></li>
          <li><a href="#section4" class="link-primary">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</a></li>
          <li><a href="#section5" class="link-primary">HOW LONG DO WE KEEP YOUR INFORMATION?</a></li>
          <li><a href="#section6" class="link-primary">DO WE COLLECT INFORMATION FROM MINORS?</a></li>
          <li><a href="#section7" class="link-primary">WHAT ARE YOUR PRIVACY RIGHTS?</a></li>
          <li><a href="#section8" class="link-primary">CONTROLS FOR DO-NOT-TRACK FEATURES</a></li>
          <li><a href="#section9" class="link-primary">DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</a></li>
          <li><a href="#section10" class="link-primary">DO OTHER REGIONS HAVE SPECIFIC PRIVACY RIGHTS?</a></li>
          <li><a href="#section11" class="link-primary">DO WE MAKE UPDATES TO THIS NOTICE?</a></li>
          <li><a href="#section12" class="link-primary">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a></li>
          <li><a href="#section13" class="link-primary">HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</a></li>
        </ol>

        <!-- Section 1 -->
        <h2 id="section1">1. WHAT INFORMATION DO WE COLLECT?</h2>
        <h3>Personal information you disclose to us</h3>
        <p><em>In Short: We collect personal information that you provide to us.</em></p>

        <p>We collect personal information that you voluntarily provide to us when you express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.</p>

        <p><strong>Sensitive Information.</strong> We do not process sensitive information.</p>

        <p>All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to such personal information.</p>

        <!-- Section 2 -->
        <h2 id="section2">2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
        <p><em>In Short: We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We process the personal information for the following purposes listed below. We may also process your information for other purposes only with your prior explicit consent.</em></p>

        <p>We process your personal information for a variety of reasons, depending on how you interact with our Services, including:</p>

        <ul>
          <li><strong>To save or protect an individual's vital interest.</strong> We may process your information when necessary to save or protect an individual's vital interest, such as to prevent harm.</li>
        </ul>

        <!-- Section 3 -->
        <h2 id="section3">3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?</h2>
        <p><em>In Short: We only process your personal information when we believe it is necessary and we have a valid legal reason (i.e., legal basis) to do so under applicable law, like with your consent, to comply with laws, to provide you with services to enter into or fulfill our contractual obligations, to protect your rights, or to fulfill our legitimate business interests.</em></p>

        <h3>If you are located in the EU or UK, this section applies to you.</h3>

        <p>The General Data Protection Regulation (GDPR) and UK GDPR require us to explain the valid legal bases we rely on in order to process your personal information. As such, we may rely on the following legal bases to process your personal information:</p>

        <ul>
          <li><strong>Consent.</strong> We may process your information if you have given us permission (i.e., consent) to use your personal information for a specific purpose. You can withdraw your consent at any time. Learn more about <a href="#section7" class="link-primary">withdrawing your consent</a>.</li>
          <li><strong>Legal Obligations.</strong> We may process your information where we believe it is necessary for compliance with our legal obligations, such as to cooperate with a law enforcement body or regulatory agency, exercise or defend our legal rights, or disclose your information as evidence in litigation in which we are involved.</li>
          <li><strong>Vital Interests.</strong> We may process your information where we believe it is necessary to protect your vital interests or the vital interests of a third party, such as situations involving potential threats to the safety of any person.</li>
        </ul>

        <h3>If you are located in Canada, this section applies to you.</h3>

        <p>We may process your information if you have given us specific permission (i.e., express consent) to use your personal information for a specific purpose, or in situations where your permission can be inferred (i.e., implied consent). You can <a href="#section7" class="link-primary">withdraw your consent</a> at any time.</p>

        <p>In some exceptional cases, we may be legally permitted under applicable law to process your information without your consent, including, for example:</p>

        <ul>
          <li>If collection is clearly in the interests of an individual and consent cannot be obtained in a timely way</li>
          <li>For investigations and fraud detection and prevention</li>
          <li>For business transactions provided certain conditions are met</li>
          <li>If it is contained in a witness statement and the collection is necessary to assess, process, or settle an insurance claim</li>
          <li>For identifying injured, ill, or deceased persons and communicating with next of kin</li>
          <li>If we have reasonable grounds to believe an individual has been, is, or may be victim of financial abuse</li>
          <li>If it is reasonable to expect collection and use with consent would compromise the availability or the accuracy of the information and the collection is reasonable for purposes related to investigating a breach of an agreement or a contravention of the laws of Canada or a province</li>
          <li>If disclosure is required to comply with a subpoena, warrant, court order, or rules of the court relating to the production of records</li>
          <li>If it was produced by an individual in the course of their employment, business, or profession and the collection is consistent with the purposes for which the information was produced</li>
          <li>If the collection is solely for journalistic, artistic, or literary purposes</li>
          <li>If the information is publicly available and is specified by the regulations</li>
          <li>We may disclose de-identified information for approved research or statistics projects, subject to ethics oversight and confidentiality commitments</li>
        </ul>

        <!-- Section 4 -->
        <h2 id="section4">4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h2>
        <p><em>In Short: We may share information in specific situations described in this section and/or with the following third parties.</em></p>

        <p>We may need to share your personal information in the following situations:</p>

        <ul>
          <li><strong>Business Transfers.</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</li>
        </ul>

        <!-- Section 5 -->
        <h2 id="section5">5. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
        <p><em>In Short: We keep your information for as long as necessary to fulfill the purposes outlined in this Privacy Notice unless otherwise required by law.</em></p>

        <p>We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements).</p>

        <p>When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.</p>

        <!-- Section 6 -->
        <h2 id="section6">6. DO WE COLLECT INFORMATION FROM MINORS?</h2>
        <p><em>In Short: We do not knowingly collect data from or market to children under 18 years of age or the equivalent age as specified by law in your jurisdiction.</em></p>

        <p>We do not knowingly collect, solicit data from, or market to children under 18 years of age or the equivalent age as specified by law in your jurisdiction, nor do we knowingly sell such personal information. By using the Services, you represent that you are at least 18 or the equivalent age as specified by law in your jurisdiction or that you are the parent or guardian of such a minor and consent to such minor dependent's use of the Services. If we learn that personal information from users less than 18 years of age or the equivalent age as specified by law in your jurisdiction has been collected, we will deactivate the account and take reasonable measures to promptly delete such data from our records. If you become aware of any data we may have collected from children under age 18 or the equivalent age as specified by law in your jurisdiction, please contact us at <a href="mailto:comments@organlive.com" class="link-primary">comments@organlive.com</a>.</p>

        <!-- Section 7 -->
        <h2 id="section7">7. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
        <p><em>In Short: Depending on your state of residence in the US or in some regions, such as the European Economic Area (EEA), United Kingdom (UK), Switzerland, and Canada, you have rights that allow you greater access to and control over your personal information. You may review, change, or terminate your account at any time, depending on your country, province, or state of residence.</em></p>

        <p>In some regions (like the EEA, UK, Switzerland, and Canada), you have certain rights under applicable data protection laws. These may include the right (i) to request access and obtain a copy of your personal information, (ii) to request rectification or erasure; (iii) to restrict the processing of your personal information; (iv) if applicable, to data portability; and (v) not to be subject to automated decision-making. If a decision that produces legal or similarly significant effects is made solely by automated means, we will inform you, explain the main factors, and offer a simple way to request human review. In certain circumstances, you may also have the right to object to the processing of your personal information. You can make such a request by contacting us by using the contact details provided in the section <a href="#section12" class="link-primary">"HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"</a> below.</p>

        <p>We will consider and act upon any request in accordance with applicable data protection laws.</p>

        <p>If you are located in the EEA or UK and you believe we are unlawfully processing your personal information, you also have the right to complain to your Member State data protection authority or UK data protection authority.</p>

        <p>If you are located in Switzerland, you may contact the Federal Data Protection and Information Commissioner.</p>

        <p><strong>Withdrawing your consent:</strong> If we are relying on your consent to process your personal information, which may be express and/or implied consent depending on the applicable law, you have the right to withdraw your consent at any time. You can withdraw your consent at any time by contacting us by using the contact details provided in the section <a href="#section12" class="link-primary">"HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"</a> below.</p>

        <p>However, please note that this will not affect the lawfulness of the processing before its withdrawal nor, when applicable law allows, will it affect the processing of your personal information conducted in reliance on lawful processing grounds other than consent.</p>

        <p>If you have questions or comments about your privacy rights, you may email us at <a href="mailto:comments@organlive.com" class="link-primary">comments@organlive.com</a>.</p>

        <!-- Section 8 -->
        <h2 id="section8">8. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
        <p>Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. At this stage, no uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we must follow in the future, we will inform you about that practice in a revised version of this Privacy Notice.</p>

        <p>California law requires us to let you know how we respond to web browser DNT signals. Because there currently is not an industry or legal standard for recognizing or honoring DNT signals, we do not respond to them at this time.</p>

        <!-- Section 9 -->
        <h2 id="section9">9. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</h2>
        <p><em>In Short: If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have the right to request access to and receive details about the personal information we maintain about you and how we have processed it, correct inaccuracies, get a copy of, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law. More information is provided below.</em></p>

        <h3>Categories of Personal Information We Collect</h3>
        <p>The table below shows the categories of personal information we have collected in the past twelve (12) months. The table includes illustrative examples of each category and does not reflect the personal information we collect from you. For a comprehensive inventory of all personal information we process, please refer to the section <a href="#section1" class="link-primary">"WHAT INFORMATION DO WE COLLECT?"</a></p>

        <div class="table-responsive">
          <table class="privacy-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Examples</th>
                <th>Collected</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>A. Identifiers</td>
                <td>Contact details, such as real name, alias, postal address, telephone or mobile contact number, unique personal identifier, online identifier, Internet Protocol address, email address, and account name</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>B. Personal information as defined in the California Customer Records statute</td>
                <td>Name, contact information, education, employment, employment history, and financial information</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>C. Protected classification characteristics under state or federal law</td>
                <td>Gender, age, date of birth, race and ethnicity, national origin, marital status, and other demographic data</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>D. Commercial information</td>
                <td>Transaction information, purchase history, financial details, and payment information</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>E. Biometric information</td>
                <td>Fingerprints and voiceprints</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>F. Internet or other similar network activity</td>
                <td>Browsing history, search history, online behavior, interest data, and interactions with our and other websites, applications, systems, and advertisements</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>G. Geolocation data</td>
                <td>Device location</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>H. Audio, electronic, sensory, or similar information</td>
                <td>Images and audio, video or call recordings created in connection with our business activities</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>I. Professional or employment-related information</td>
                <td>Business contact details in order to provide you our Services at a business level or job title, work history, and professional qualifications if you apply for a job with us</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>J. Education Information</td>
                <td>Student records and directory information</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>K. Inferences drawn from collected personal information</td>
                <td>Inferences drawn from any of the collected personal information listed above to create a profile or summary about, for example, an individual's preferences and characteristics</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>L. Sensitive personal Information</td>
                <td></td>
                <td>NO</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>We may also collect other personal information outside of these categories through instances where you interact with us in person, online, or by phone or mail in the context of:</p>

        <ul>
          <li>Receiving help through our customer support channels;</li>
          <li>Participation in customer surveys or contests; and</li>
          <li>Facilitation in the delivery of our Services and to respond to your inquiries.</li>
        </ul>

        <h3>Sources of Personal Information</h3>
        <p>Learn more about the sources of personal information we collect in <a href="#section1" class="link-primary">"WHAT INFORMATION DO WE COLLECT?"</a></p>

        <h3>How We Use and Share Personal Information</h3>
        <p>Learn more about how we use your personal information in the section, <a href="#section2" class="link-primary">"HOW DO WE PROCESS YOUR INFORMATION?"</a></p>

        <p><strong>Will your information be shared with anyone else?</strong></p>

        <p>We may disclose your personal information with our service providers pursuant to a written contract between us and each service provider. Learn more about how we disclose personal information to in the section, <a href="#section4" class="link-primary">"WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?"</a></p>

        <p>We may use your personal information for our own business purposes, such as for undertaking internal research for technological development and demonstration. This is not considered to be "selling" of your personal information.</p>

        <p>We have not disclosed, sold, or shared any personal information to third parties for a business or commercial purpose in the preceding twelve (12) months. We will not sell or share personal information in the future belonging to website visitors, users, and other consumers.</p>

        <h3>Your Rights</h3>
        <p>You have rights under certain US state data protection laws. However, these rights are not absolute, and in certain cases, we may decline your request as permitted by law. These rights include:</p>

        <ul>
          <li>Right to know whether or not we are processing your personal data</li>
          <li>Right to access your personal data</li>
          <li>Right to correct inaccuracies in your personal data</li>
          <li>Right to request the deletion of your personal data</li>
          <li>Right to obtain a copy of the personal data you previously shared with us</li>
          <li>Right to non-discrimination for exercising your rights</li>
          <li>Right to opt out of the processing of your personal data if it is used for targeted advertising (or sharing as defined under California's privacy law), the sale of personal data, or profiling in furtherance of decisions that produce legal or similarly significant effects ("profiling")</li>
        </ul>

        <p>Depending upon the state where you live, you may also have the following rights:</p>

        <ul>
          <li>Right to access the categories of personal data being processed (as permitted by applicable law, including the privacy law in Minnesota)</li>
          <li>Right to obtain a list of the categories of third parties to which we have disclosed personal data (as permitted by applicable law, including the privacy law in California, Delaware, and Maryland)</li>
          <li>Right to obtain a list of specific third parties to which we have disclosed personal data (as permitted by applicable law, including the privacy law in Minnesota and Oregon)</li>
          <li>Right to obtain a list of third parties to which we have sold personal data (as permitted by applicable law, including the privacy law in Connecticut)</li>
          <li>Right to review, understand, question, and depending on where you live, correct how personal data has been profiled (as permitted by applicable law, including the privacy law in Connecticut and Minnesota)</li>
          <li>Right to limit use and disclosure of sensitive personal data (as permitted by applicable law, including the privacy law in California)</li>
          <li>Right to opt out of the collection of sensitive data and personal data collected through the operation of a voice or facial recognition feature (as permitted by applicable law, including the privacy law in Florida)</li>
        </ul>

        <h3>How to Exercise Your Rights</h3>
        <p>To exercise these rights, you can contact us by submitting a data subject access request, by emailing us at <a href="mailto:comments@organlive.com" class="link-primary">comments@organlive.com</a>, by visiting <a href="https://www.organlive.com/contact" class="link-primary">https://www.organlive.com/contact</a>, or by referring to the contact details at the bottom of this document.</p>

        <p>Under certain US state data protection laws, you can designate an authorized agent to make a request on your behalf. We may deny a request from an authorized agent that does not submit proof that they have been validly authorized to act on your behalf in accordance with applicable laws.</p>

        <h3>Request Verification</h3>
        <p>Upon receiving your request, we will need to verify your identity to determine you are the same person about whom we have the information in our system. We will only use personal information provided in your request to verify your identity or authority to make the request. However, if we cannot verify your identity from the information already maintained by us, we may request that you provide additional information for the purposes of verifying your identity and for security or fraud-prevention purposes.</p>

        <p>If you submit the request through an authorized agent, we may need to collect additional information to verify your identity before processing your request and the agent will need to provide a written and signed permission from you to submit such request on your behalf.</p>

        <h3>Appeals</h3>
        <p>Under certain US state data protection laws, if we decline to take action regarding your request, you may appeal our decision by emailing us at <a href="mailto:comments@organlive.com" class="link-primary">comments@organlive.com</a>. We will inform you in writing of any action taken or not taken in response to the appeal, including a written explanation of the reasons for the decisions. If your appeal is denied, you may submit a complaint to your state attorney general.</p>

        <!-- Section 10 -->
        <h2 id="section10">10. DO OTHER REGIONS HAVE SPECIFIC PRIVACY RIGHTS?</h2>
        <p><em>In Short: You may have additional rights based on the country you reside in.</em></p>

        <h3>Australia and New Zealand</h3>
        <p>We collect and process your personal information under the obligations and conditions set by Australia's Privacy Act 1988 and New Zealand's Privacy Act 2020 (Privacy Act).</p>

        <p>This Privacy Notice satisfies the notice requirements defined in both Privacy Acts, in particular: what personal information we collect from you, from which sources, for which purposes, and other recipients of your personal information.</p>

        <p>If you do not wish to provide the personal information necessary to fulfill their applicable purpose, it may affect our ability to provide our services, in particular:</p>

        <ul>
          <li>offer you the products or services that you want</li>
          <li>respond to or help with your requests</li>
        </ul>

        <p>At any time, you have the right to request access to or correction of your personal information. You can make such a request by contacting us by using the contact details provided in the section <a href="#section13" class="link-primary">"HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?"</a></p>

        <p>If you believe we are unlawfully processing your personal information, you have the right to submit a complaint about a breach of the Australian Privacy Principles to the Office of the Australian Information Commissioner and a breach of New Zealand's Privacy Principles to the Office of New Zealand Privacy Commissioner.</p>

        <h3>Republic of South Africa</h3>
        <p>At any time, you have the right to request access to or correction of your personal information. You can make such a request by contacting us by using the contact details provided in the section <a href="#section13" class="link-primary">"HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?"</a></p>

        <p>If you are unsatisfied with the manner in which we address any complaint with regard to our processing of personal information, you can contact the office of the regulator, the details of which are:</p>

        <p><strong>The Information Regulator (South Africa)</strong><br>
        General enquiries: <a href="mailto:enquiries@inforegulator.org.za" class="link-primary">enquiries@inforegulator.org.za</a><br>
        Complaints (complete POPIA/PAIA form 5): <a href="mailto:PAIAComplaints@inforegulator.org.za" class="link-primary">PAIAComplaints@inforegulator.org.za</a> & <a href="mailto:POPIAComplaints@inforegulator.org.za" class="link-primary">POPIAComplaints@inforegulator.org.za</a></p>

        <!-- Section 11 -->
        <h2 id="section11">11. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
        <p><em>In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws.</em></p>

        <p>We may update this Privacy Notice from time to time. The updated version will be indicated by an updated "Revised" date at the top of this Privacy Notice. If we make material changes to this Privacy Notice, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this Privacy Notice frequently to be informed of how we are protecting your information.</p>

        <!-- Section 12 -->
        <h2 id="section12">12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
        <p>If you have questions or comments about this notice, you may email us at <a href="mailto:comments@organlive.com" class="link-primary">comments@organlive.com</a> or contact us by post at:</p>

        <address>
          <strong>Organ Media Foundation</strong><br>
          6622 Michigan Ave<br>
          Saint Louis, MO 63111<br>
          United States
        </address>

        <!-- Section 13 -->
        <h2 id="section13">13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</h2>
        <p>You have the right to request access to the personal information we collect from you, details about how we have processed it, correct inaccuracies, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law. To request to review, update, or delete your personal information, please fill out and submit a data subject access request.</p>

      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPage',

  mounted () {
    document.title = 'Privacy Policy - Organlive.com'

    // Scroll to section if hash in URL
    if (this.$route.hash) {
      this.$nextTick(() => {
        const element = document.querySelector(this.$route.hash)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      })
    }
  }
}
</script>

<style scoped>
/* Privacy page wrapper */
.privacy-page-wrapper {
  padding-bottom: 180px;
}

/* Privacy page content box */
.privacy-page {
  background: var(--bg-overlay-light);
  color: var(--color-white);
  padding: var(--spacing-element);
  border-radius: var(--radius-large);
  max-width: 1000px;
  margin: 0 auto;
}

/* Page title */
.page-title {
  text-align: center;
  margin-bottom: 0.5rem;
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-white);
}

.last-updated {
  text-align: center;
  color: var(--color-medium-gray);
  font-style: italic;
  margin-bottom: 2rem;
}

/* Privacy content */
.privacy-content {
  line-height: 1.8;
  font-size: 1rem;
}

.privacy-content h2 {
  color: var(--color-white);
  font-size: 1.75rem;
  font-weight: 600;
  margin-top: 2.5rem;
  margin-bottom: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.privacy-content h3 {
  color: var(--color-white);
  font-size: 1.3rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

.privacy-content p {
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.9);
}

.privacy-content ul,
.privacy-content ol {
  margin-bottom: 1rem;
  margin-left: 1.5rem;
  color: rgba(255, 255, 255, 0.9);
}

.privacy-content li {
  margin-bottom: 0.5rem;
}

.privacy-content strong {
  color: var(--color-white);
  font-weight: 600;
}

.privacy-content em {
  color: var(--color-medium-gray);
}

/* Table of Contents */
.toc {
  background: rgba(0, 0, 0, 0.2);
  padding: 1.5rem 1.5rem 1.5rem 2.5rem;
  border-radius: var(--radius-default);
  margin: 1.5rem 0;
}

.toc li {
  margin-bottom: 0.75rem;
}

/* Address styling */
address {
  font-style: normal;
  margin: 1rem 0;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: var(--radius-default);
  border-left: 3px solid var(--color-primary);
}

/* Table styling */
.table-responsive {
  overflow-x: auto;
  margin: 1.5rem 0;
}

.privacy-table {
  width: 100%;
  border-collapse: collapse;
  background: rgba(0, 0, 0, 0.2);
  border-radius: var(--radius-default);
}

.privacy-table th,
.privacy-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.privacy-table th {
  background: rgba(0, 0, 0, 0.3);
  color: var(--color-white);
  font-weight: 600;
}

.privacy-table td {
  color: rgba(255, 255, 255, 0.9);
}

.privacy-table tr:last-child td {
  border-bottom: none;
}

/* Responsive */
@media (max-width: 768px) {
  .privacy-page-wrapper {
    padding-bottom: 200px;
  }

  .page-title {
    font-size: 2rem;
  }

  .privacy-content h2 {
    font-size: 1.5rem;
  }

  .privacy-content h3 {
    font-size: 1.2rem;
  }

  .privacy-table {
    font-size: 0.9rem;
  }

  .privacy-table th,
  .privacy-table td {
    padding: 0.75rem 0.5rem;
  }
}

@media (max-width: 480px) {
  .privacy-page-wrapper {
    padding-bottom: 220px;
  }

  .page-title {
    font-size: 1.75rem;
  }

  /* Make table scroll on very small screens */
  .privacy-table {
    font-size: 0.85rem;
  }
}
</style>
